import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ProductCard } from "../components/products/ProductCard";
import { trackEvent, toGaItem } from "../lib/analyticsEvents";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import api from "../lib/api";

export default function MenuPage() {
	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);

	const [searchQuery, setSearchQuery] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedCategory, setSelectedCategory] = useState("all");
	const [selectedTag, setSelectedTag] = useState("all");
	const [sortBy, setSortBy] = useState("default");

	const tags = [
		{ id: "populaire", name: "Populaire" },
		{ id: "nouveau", name: "Nouveau" },
		{ id: "vegan", name: "Vegan" },
		{ id: "sans-gluten", name: "Sans Gluten" },
		{ id: "bio", name: "Bio" },
	];

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);

			try {
				const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

				const cachedProducts = sessionStorage.getItem("menu_products");
				const cachedCategories = sessionStorage.getItem("menu_categories");
				const cachedAt = sessionStorage.getItem("menu_products_at");
				const isExpired =
					!cachedAt || Date.now() - Number(cachedAt) > CACHE_TTL;

				if (cachedProducts && cachedCategories && !isExpired) {
					setCategories(JSON.parse(cachedCategories));
					setProducts(
						JSON.parse(cachedProducts).filter((product) => product.available),
					);
					setLoading(false);
					return;
				}

				const [categoriesData, productsData] = await Promise.all([
					api.getCategories(),
					api.getProducts(),
				]);

				setCategories(categoriesData || []);
				setProducts(
					(productsData || []).filter((product) => product.available),
				);

				sessionStorage.setItem(
					"menu_categories",
					JSON.stringify(categoriesData || []),
				);
				sessionStorage.setItem(
					"menu_products",
					JSON.stringify(productsData || []),
				);
				sessionStorage.setItem("menu_products_at", String(Date.now()));
			} catch (error) {
				console.error("Erreur API :", error);
				setCategories([]);
				setProducts([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const filteredProducts = useMemo(() => {
		let result = [...products];

		if (debouncedSearch) {
			const query = debouncedSearch.toLowerCase();
			result = result.filter(
				(product) =>
					product.name?.toLowerCase().includes(query) ||
					product.short_description?.toLowerCase().includes(query) ||
					product.description?.toLowerCase().includes(query),
			);
		}

		// ✅ FIX IMPORTANT : catégorie basée sur slug
		if (selectedCategory !== "all") {
			result = result.filter(
				(product) => product.category_slug === selectedCategory,
			);
		}

		if (selectedTag !== "all") {
			result = result.filter((product) => product.tags?.includes(selectedTag));
		}

		switch (sortBy) {
			case "price-asc":
				result.sort((a, b) => a.price - b.price);
				break;
			case "price-desc":
				result.sort((a, b) => b.price - a.price);
				break;
			case "name":
				result.sort((a, b) => a.name.localeCompare(b.name));
				break;
			default:
				result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
		}

		return result;
	}, [products, debouncedSearch, selectedCategory, selectedTag, sortBy]);

	useEffect(() => {
		if (loading || !filteredProducts.length) return;

		trackEvent("view_item_list", {
			item_list_id: "menu",
			item_list_name: "Menu",
			items: filteredProducts.map((product) => toGaItem(product, 1)),
		});
	}, [filteredProducts, loading]);

	const clearFilters = () => {
		setSearchQuery("");
		setSelectedCategory("all");
		setSelectedTag("all");
		setSortBy("default");
	};

	const hasActiveFilters =
		searchQuery ||
		selectedCategory !== "all" ||
		selectedTag !== "all" ||
		sortBy !== "default";

	const pageTitle = "Menu & Boutique | Léger's Coffee Genève";
	const pageDescription =
		"Découvrez le menu de Léger's Coffee à Genève : cafés de spécialité, boissons, viennoiseries fraîches et pâtisseries artisanales disponibles en boutique.";
	const canonicalUrl = "https://legerscoffee.fr/menu";
	const ogImage = "https://legerscoffee.fr/og-image.jpg";

	const structuredDataCollectionPage = {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		name: "Menu & Boutique | Léger's Coffee",
		url: canonicalUrl,
		description: pageDescription,
		inLanguage: "fr-CH",
	};

	const structuredDataMenu = {
		"@context": "https://schema.org",
		"@type": "Menu",
		name: "Menu Léger's Coffee",
		description: pageDescription,
		hasMenuSection: categories.map((category) => ({
			"@type": "MenuSection",
			name: category.name,
			description: category.description || category.name,
		})),
	};

	return (
		<>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<meta name="robots" content="index, follow" />
				<link rel="canonical" href={canonicalUrl} />
			</Helmet>

			<main data-testid="menu-page" className="min-h-screen pt-20">
				<section className="bg-warm-secondary py-16 lg:py-24">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						<div className="text-center max-w-2xl mx-auto">
							<h1 className="font-serif text-4xl lg:text-5xl text-espresso font-semibold mb-6">
								Menu & Boutique
							</h1>
						</div>
					</div>
				</section>

				<section className="sticky top-20 z-40 bg-warm-alabaster border-b border-border-subtle py-4">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						<div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
							<div className="relative w-full lg:w-80">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
								<Input
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Rechercher un produit..."
								/>
							</div>

							<div className="flex flex-wrap gap-3 items-center">
								<Select
									value={selectedCategory}
									onValueChange={setSelectedCategory}
								>
									<SelectTrigger className="w-40 h-12">
										<SelectValue placeholder="Catégorie" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">Toutes catégories</SelectItem>

										{/* ✅ FIX : on utilise slug */}
										{categories.map((cat) => (
											<SelectItem key={cat.id} value={cat.slug}>
												{cat.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>

								<Select value={selectedTag} onValueChange={setSelectedTag}>
									<SelectTrigger className="w-36 h-12">
										<SelectValue placeholder="Filtres" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">Tous</SelectItem>
										{tags.map((tag) => (
											<SelectItem key={tag.id} value={tag.id}>
												{tag.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>

								<Select value={sortBy} onValueChange={setSortBy}>
									<SelectTrigger className="w-40 h-12">
										<SelectValue placeholder="Trier" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="default">Recommandés</SelectItem>
										<SelectItem value="price-asc">Prix croissant</SelectItem>
										<SelectItem value="price-desc">Prix décroissant</SelectItem>
										<SelectItem value="name">Nom A-Z</SelectItem>
									</SelectContent>
								</Select>

								{hasActiveFilters && (
									<Button variant="ghost" onClick={clearFilters}>
										<X className="w-4 h-4 mr-2" />
										Effacer
									</Button>
								)}
							</div>
						</div>
					</div>
				</section>

				<section className="py-16 lg:py-24 bg-warm-alabaster">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						{loading ? (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
								{Array.from({ length: 8 }).map((_, i) => (
									<div key={i} className="animate-pulse">
										<div className="aspect-[4/5] bg-warm-secondary mb-4 rounded" />
										<div className="h-4 bg-warm-secondary rounded mb-2 w-3/4" />
										<div className="h-3 bg-warm-secondary rounded w-1/2" />
									</div>
								))}
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
								{filteredProducts.map((product, index) => (
									<ProductCard
										key={product.id}
										product={product}
										index={index}
									/>
								))}
							</div>
						)}
					</div>
				</section>
			</main>
		</>
	);
}
